import React, { useState, useEffect } from "react";
import { addBlog, deleteBlog, getAllBlogs, updateBlog } from "../services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

const AdminPanel = () => {
  const [blog, setBlog] = useState({ title: "", content: "", category: "", images: [] });
  const [imageFiles, setImageFiles] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const fetchBlogs = async () => {
    try {
      const data = await getAllBlogs(page, "", search);
      setBlogs(data.blogs || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setBlogs([]);
    }
  };

  const handleChange = (e) => setBlog({ ...blog, [e.target.name]: e.target.value });
  const handleImageChange = (e) => setImageFiles([...e.target.files]);

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`https://shivansh-portfolio-nkyo.onrender.com/api/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to upload image");

      const data = await res.json();
      return data.imageUrl;
    } catch (error) {
      console.error("Image Upload Error:", error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let imageUrls = [];
    if (imageFiles.length > 0) {
      imageUrls = await Promise.all(imageFiles.map((img) => handleImageUpload(img)));
      imageUrls = imageUrls.filter((url) => url !== null);
    }

    try {
      if (editMode) {
        await updateBlog(editId, { 
          ...blog, 
          images: imageUrls.length > 0 ? imageUrls : blog.images 
        });
        alert("Blog Updated!");
      } else {
        await addBlog({ ...blog, images: imageUrls });
        alert("Blog Added!");
      }

      setBlog({ title: "", content: "", category: "", images: [] });
      setImageFiles([]);
      setEditMode(false);
      setEditId(null);
      fetchBlogs();
    } catch (error) {
      alert("Failed to save blog. Please try again.");
    }
  };

  const handleEdit = (b) => {
    setBlog({ title: b.title, content: b.content, category: b.category, images: b.images });
    setEditMode(true);
    setEditId(b._id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        await deleteBlog(id);
        alert("Blog Deleted!");
        fetchBlogs();
      } catch (error) {
        alert("Failed to delete blog.");
      }
    }
  };

  return (
    <div className="p-10 bg-gray-900 text-white">
      <h2 className="text-3xl font-bold mb-6">📝 {editMode ? "Edit Blog" : "Add Blog"}</h2>

      {/* ✅ Search Bar */}
      <input
        type="text"
        placeholder="🔍 Search Blogs..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full p-3 bg-gray-800 text-white rounded-md mb-4"
      />

      {/* ✅ Blog Form */}
      <form onSubmit={handleSubmit} className="mb-10">
        <input
          type="text"
          name="title"
          placeholder="Title"
          className="w-full p-3 bg-gray-800 rounded-md mb-2"
          onChange={handleChange}
          value={blog.title}
          required
        />
        <textarea
          name="content"
          placeholder="Write in Markdown...  (supports **bold**, *italic*, ```code```, > quotes, tables, lists)"
          className="w-full p-3 bg-gray-800 rounded-md mb-2 font-mono text-sm"
          onChange={handleChange}
          value={blog.content}
          required
          rows={12}
          style={{ display: showPreview ? 'none' : 'block' }}
        />

        {/* Live Preview */}
        {showPreview && (
          <div className="w-full p-4 bg-gray-800 rounded-md mb-2 min-h-[300px] max-h-[500px] overflow-y-auto prose-invert">
            {blog.content ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-4 mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-bold text-white mt-3 mb-2">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-2 mb-1">{children}</h3>,
                  p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-3 text-sm">{children}</p>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  a: ({ href, children }) => <a href={href} className="text-blue-400 underline">{children}</a>,
                  code({ inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline && match ? (
                      <SyntaxHighlighter style={oneDark} language={match[1]} customStyle={{ fontSize: '12px', borderRadius: '6px' }} {...props}>
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code className="bg-gray-700 text-purple-300 px-1 rounded text-xs">{children}</code>
                    );
                  },
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-purple-500/50 pl-3 text-gray-400 italic my-2">{children}</blockquote>,
                  ul: ({ children }) => <ul className="list-disc list-inside text-gray-300 text-sm space-y-1 mb-3">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside text-gray-300 text-sm space-y-1 mb-3">{children}</ol>,
                  table: ({ children }) => <div className="overflow-x-auto my-3"><table className="w-full text-sm border border-gray-700">{children}</table></div>,
                  th: ({ children }) => <th className="px-3 py-1.5 bg-gray-700 text-left text-xs text-gray-300">{children}</th>,
                  td: ({ children }) => <td className="px-3 py-1.5 border-t border-gray-700 text-gray-400 text-xs">{children}</td>,
                }}
              >
                {blog.content}
              </ReactMarkdown>
            ) : (
              <p className="text-gray-600 text-sm italic">Start writing markdown to see preview...</p>
            )}
          </div>
        )}

        {/* Write / Preview toggle */}
        <div className="flex gap-2 mb-3">
          <button type="button" onClick={() => setShowPreview(false)}
            className={`px-3 py-1 text-xs rounded ${!showPreview ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400'}`}
          >Write</button>
          <button type="button" onClick={() => setShowPreview(true)}
            className={`px-3 py-1 text-xs rounded ${showPreview ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400'}`}
          >Preview</button>
          <span className="text-gray-600 text-xs ml-auto self-center font-mono">
            {blog.content.split(/\s+/).filter(Boolean).length} words
          </span>
        </div>
        <input
          type="text"
          name="category"
          placeholder="Category"
          className="w-full p-3 bg-gray-800 rounded-md mb-2"
          onChange={handleChange}
          value={blog.category}
          required
        />
        <input type="file" multiple onChange={handleImageChange} className="w-full p-3 bg-gray-800 rounded-md mb-2" />

        {/* ✅ Display Existing Images (If Editing) */}
        {editMode && blog.images.length > 0 && (
          <div className="flex flex-wrap gap-2 my-3">
            {blog.images.map((img, index) => (
              <img key={index} src={img} alt="Blog" className="w-24 h-24 object-cover rounded-md border" />
            ))}
          </div>
        )}

        <button type="submit" className="bg-blue-500 p-3 w-full rounded-md">
          {editMode ? "Update Blog" : "Add Blog"}
        </button>
      </form>

      {/* ✅ Existing Blogs */}
      <h2 className="text-2xl font-bold mt-10 mb-4">📌 Existing Blogs</h2>
      {blogs.length === 0 ? (
        <p className="text-center text-gray-400">No blogs available.</p>
      ) : (
        blogs.map((b) => (
          <div key={b._id} className="bg-gray-800 p-4 rounded-md my-4 flex justify-between items-center">
            <div>
              <h3 className="text-xl">{b.title}</h3>
              <p className="text-gray-400">{b.category}</p>
            </div>
            <div>
              <button onClick={() => handleEdit(b)} className="bg-yellow-500 px-3 py-1 rounded-md mx-2">✏️ Edit</button>
              <button onClick={() => handleDelete(b._id)} className="bg-red-500 px-3 py-1 rounded-md">🗑 Delete</button>
            </div>
          </div>
        ))
      )}

      {/* ✅ Pagination Controls */}
      <div className="flex justify-center mt-8 space-x-4">
        <button
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          disabled={page === 1}
          className="px-4 py-2 bg-gray-700 rounded-md text-white disabled:opacity-50"
        >
          Prev
        </button>

        <span className="text-white text-lg">Page {page} of {totalPages}</span>

        <button
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          className="px-4 py-2 bg-gray-700 rounded-md text-white disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminPanel;
